/**
 * @file Pinia store for UI notifications (toasts/snackbars).
 * @description
 * Maintains a reactive queue of notifications to display user feedback (success, error, info, warning).
 * Provides helpers to add and auto-remove items after a timeout.
 */
import { defineStore } from "pinia";
import { ref, Ref } from "vue";

export type NotificationType = "info" | "success" | "warning" | "error";

export interface Notification {
  id: number;
  message: string;
  type: NotificationType;
  timeout?: number; // timeout est optionnel, 0 signifie pas de timeout automatique
}

export interface NewNotification {
  message: string;
  type?: NotificationType;
  timeout?: number;
}

export const useNotificationStore = defineStore("notification", () => {
  const notifications: Ref<Notification[]> = ref([]);
  // Compteur monotone : `Date.now()` faisait collisionner deux notifications
  // ajoutées dans la même milliseconde (ex. Promise.all d'appels en échec),
  // si bien que `removeNotification`/le timeout retiraient la mauvaise entrée.
  let nextId = 0;

  function addNotification(notification: NewNotification): number {
    const id = ++nextId;
    const newNotification: Notification = {
      id,
      message: notification.message,
      type: notification.type || "info",
      timeout: notification.timeout === undefined ? 5000 : notification.timeout, // Défaut à 5000 si undefined
    };
    notifications.value.push(newNotification);

    if (newNotification.timeout !== 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.timeout);
    }
    return id;
  }

  function removeNotification(id: number): void {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index !== -1) {
      notifications.value.splice(index, 1);
    }
  }

  function success(message: string, timeout?: number): number {
    return addNotification({ message, type: "success", timeout });
  }

  function error(message: string, timeout?: number): number {
    return addNotification({ message, type: "error", timeout });
  }

  function warning(message: string, timeout?: number): number {
    return addNotification({ message, type: "warning", timeout });
  }

  function info(message: string, timeout?: number): number {
    return addNotification({ message, type: "info", timeout });
  }

  return {
    notifications,
    addNotification,
    removeNotification,
    success,
    error,
    warning,
    info,
  };
});
